import { useEffect, useRef, useState } from 'react';
import { useIOSPresentation } from '../lib/web-presentation';
import { presentAppRecordCopy } from '../features/shared/record-copy';
import { classificationSourceUnavailable } from '../features/shared/evidence-results';
import { ReturnCentre, configuredObligationsForSubject, filingDeadlineNoticesForSubject, returnContextForObligation, toReturnCentreObligation,
  type TaxObligationId } from '../features/return-centre';
import { bindBusinessCitFacts, bindBusinessVatFacts, BusinessCitReturnInteraction, BusinessVatReturnInteraction,
  createOwnBusinessInvoiceDraft, formatKobo,
  mutateOwnBusinessInvoiceDraft, OwnBusinessInvoiceInteraction, requestOwnBusinessInvoiceMock, TrackB1Journey, trackB1Fixture,
  type BusinessCitFactsInput, type BusinessVatFactsInput, type MockInvoiceReceiptView,
  type TrackB1Model } from '../features/track-b1';
import { applyEvidenceDuplicatePresentation, applyEvidenceProposalBlockers, applyEvidenceReviewProgress, applyExtractedOwnership, detectEvidenceOwner, evaluateExtractedOwnership, reconciliationFromResult,
  mergeRecoveredEvidence, restorePersistedEvidence, settleEvidence, settleEvidenceWorkflowError,
  purgeEvidenceUsage, type SharedJourneyPorts } from '../features/shared';
import { fileToBase64, type CitPositionResult, type EInvoiceResource, type LocalInvoiceEnvelope, type ReviewedInvoiceFactsInput, type TaxCalculationResult,
  type TaxReturnResource } from '../lib/api';
import type { PersonaWorkspaceProps } from '../types/persona';
import { Button, Select, WorkspaceContextHeader } from '../components';

export function BusinessWorkspace({ api, subjects }: PersonaWorkspaceProps) {
  const businesses = subjects.filter((subject) => subject.kind === 'business');
  const [selectedId, setSelectedId] = useState(businesses[0]?.id ?? '');
  const selected = businesses.find((subject) => subject.id === selectedId) ?? businesses[0];
  const activeTaxYear = selected?.resolved_contexts?.find((context) => context.scope.rail === 'tax_return')
    ?.scope.periodId.match(/\d{4}/)?.[0] ?? 'Not set';
  const selectBusiness = (id: string) => {
    const subject = businesses.find((candidate) => candidate.id === id);
    if (!subject) return;
    api.selectIdentityContexts(subject.context_ids ?? {});
    setSelectedId(subject.id);
  };
  return <>
    <WorkspaceContextHeader contextIcon="building" contextLabel="Business"
      contextOptions={businesses.map((business) => ({ label: business.display_name, value: business.id }))}
      contextValue={selected?.id ?? ''} onContextChange={selectBusiness}
      periodLabel="Tax year" periodValue={activeTaxYear} />
    {selected && <BusinessTaxWorkspace key={selected.id} api={api} subjects={[selected]} />}
  </>;
}

function BusinessTaxWorkspace({ api, subjects }: PersonaWorkspaceProps) {
  const ios = useIOSPresentation();
  const business = subjects.find((subject) => subject.kind === 'business');
  const [model, setModel] = useState<TrackB1Model>(() => buildInitialBusinessModel(business));
  const [notice, setNotice] = useState('');
  const [invoice, setInvoice] = useState<EInvoiceResource>();
  const [receipt, setReceipt] = useState<MockInvoiceReceiptView>();
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceRestoring, setInvoiceRestoring] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState<EInvoiceResource[]>([]);
  const [localInvoice, setLocalInvoice] = useState<LocalInvoiceEnvelope>();
  const invoiceContext = business?.resolved_contexts?.find((context) => context.scope.rail === 'e_invoice');
  const invoiceSequence = useRef(0);
  const invoiceMounted = useRef(true);
  useEffect(() => { invoiceMounted.current = true; return () => { invoiceMounted.current = false; invoiceSequence.current += 1; }; }, []);
  const invoiceCurrent = (sequence: number) => invoiceMounted.current && invoiceSequence.current === sequence;
  const applyLocalInvoice = (value: LocalInvoiceEnvelope) => {
    setInvoice(value.invoice); setLocalInvoice(value); setReceipt(undefined);
    setSavedInvoices((current) => [value.invoice, ...current.filter((item) => item.id !== value.invoice.id)]);
    const submission = value.submission;
    if (!submission) return;
    setModel((current) => ({ ...current,
      invoices: current.invoices.map((item) => item.id === value.invoice.id ? { ...item,
        state: submission.state === 'REJECTED' ? 'blocked' : submission.state === 'ACCEPTED' ? 'bridge-mock-received' : 'queued-to-bridge-mock',
        issue: submission.message } : item),
      bridge: { ...current.bridge, detail: submission.message, retryAllowed: submission.retryable,
        reconciliationRequired: submission.state === 'RESPONSE_UNKNOWN' },
      submission: [...current.submission.filter((event) => !event.id.startsWith(`invoice:${value.invoice.id}:`)),
        ...submission.events.map((event, index) => ({ id: `invoice:${value.invoice.id}:${index}`, label: 'Invoice delivery',
          owner: 'Receiving service simulator', state: event.state === 'REJECTED' ? 'failed' as const
            : event.state === 'RESPONSE_UNKNOWN' ? 'ambiguous' as const : event.state === 'ACCEPTED' ? 'complete' as const : 'current' as const,
          occurredAt: event.occurredAt, detail: event.message }))] }));
  };

  useEffect(() => {
    const sequence = ++invoiceSequence.current;
    setInvoice(undefined); setLocalInvoice(undefined); setReceipt(undefined); setSavedInvoices([]);
    setInvoiceBusy(false); setInvoiceRestoring(false);
    if (!business || !invoiceContext || typeof api.listInvoices !== 'function') return;
    setInvoiceRestoring(true);
    const controller = new AbortController();
    void api.listInvoices(business.id, controller.signal, invoiceContext.contextId).then(async ({ invoices }) => {
      if (controller.signal.aborted || !invoiceCurrent(sequence)) return;
      const scoped = invoices.filter((item) => item.track === 'B1' && item.taxpayerId === business.id
        && item.engagementId === invoiceContext.scope.engagementId && item.packageId === invoiceContext.scope.packageId
        && item.periodId === invoiceContext.scope.periodId);
      setSavedInvoices(scoped); setInvoice(scoped[0]);
      setModel((current) => ({ ...current, invoices: scoped.map((item) => ({ id: item.id,
        buyerName: 'Saved invoice', invoiceNumber: item.invoiceNumber, amountLabel: formatKobo(item.payableAmountKobo),
        state: item.state === 'APPROVED' ? 'validated-for-mock' : 'needs-review' })) }));
      if (scoped[0] && typeof api.getLocalInvoice === 'function') {
        const value = await api.getLocalInvoice(scoped[0].id, invoiceContext.contextId, controller.signal);
        if (!controller.signal.aborted && invoiceCurrent(sequence)) applyLocalInvoice(value);
      }
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && invoiceCurrent(sequence)) {
        setNotice(error instanceof Error ? error.message : 'Saved invoice delivery could not be loaded.');
      }
    }).finally(() => { if (!controller.signal.aborted && invoiceCurrent(sequence)) setInvoiceRestoring(false); });
    return () => controller.abort();
  }, [api, business?.id, invoiceContext?.contextId]);

  useEffect(() => {
    if (!localInvoice?.canRefresh || !localInvoice.submission || !invoiceContext || invoiceBusy) return;
    const controller = new AbortController();
    const sequence = invoiceSequence.current;
    const timer = setTimeout(() => {
      void api.refreshLocalInvoice(localInvoice.invoice.id, invoiceContext.contextId, controller.signal).then((value) => {
        if (!controller.signal.aborted && invoiceCurrent(sequence)) applyLocalInvoice(value);
      }).catch((error: unknown) => {
        if (!controller.signal.aborted && invoiceCurrent(sequence)) setNotice(error instanceof Error ? error.message : 'Delivery status could not be checked.');
      });
    }, Math.min(60_000, Math.max(1_000, localInvoice.submission.nextPollAfterMs ?? 2_000)));
    return () => { controller.abort(); clearTimeout(timer); };
  }, [api, localInvoice, invoiceContext?.contextId, invoiceBusy]);

  const selectSavedInvoice = async (id: string) => {
    const selected = savedInvoices.find((item) => item.id === id);
    if (!selected || !invoiceContext || invoiceBusy) return;
    const sequence = ++invoiceSequence.current;
    setInvoice(selected); setLocalInvoice(undefined); setReceipt(undefined); setInvoiceBusy(true);
    try {
      if (typeof api.getLocalInvoice === 'function') {
        const value = await api.getLocalInvoice(id, invoiceContext.contextId);
        if (invoiceCurrent(sequence)) applyLocalInvoice(value);
      }
    } catch (error) { if (invoiceCurrent(sequence)) setNotice(error instanceof Error ? error.message : 'Invoice delivery could not be loaded.'); }
    finally { if (invoiceCurrent(sequence)) setInvoiceBusy(false); }
  };
  const refreshInvoice = async (retry: boolean) => {
    if (!invoice || !invoiceContext || invoiceBusy) return;
    const sequence = ++invoiceSequence.current;
    setInvoiceBusy(true);
    try {
      const value = retry ? await api.retryLocalInvoice(invoice.id, invoiceContext.contextId)
        : await api.refreshLocalInvoice(invoice.id, invoiceContext.contextId);
      if (invoiceCurrent(sequence)) applyLocalInvoice(value);
    } catch (error) { if (invoiceCurrent(sequence)) setNotice(error instanceof Error ? error.message : 'Invoice delivery could not be checked.'); }
    finally { if (invoiceCurrent(sequence)) setInvoiceBusy(false); }
  };
  const [taxReturn, setTaxReturn] = useState<TaxReturnResource>();
  const [vatCalculation, setVatCalculation] = useState<TaxCalculationResult>();
  const [vatSnapshotSha256, setVatSnapshotSha256] = useState<string>();
  const [vatBusy, setVatBusy] = useState(false);
  const [citCalculation, setCitCalculation] = useState<CitPositionResult>();
  const [citBusy, setCitBusy] = useState(false);
  const [selectedReturnObligation, setSelectedReturnObligation] = useState<TaxObligationId>('VAT');
  const evidenceContext = returnContextForObligation(business, selectedReturnObligation)
    ?? business?.resolved_contexts?.find((context) => context.scope.rail === 'tax_return');

  useEffect(() => {
    if (!business || !evidenceContext || typeof api.listClassificationJobs !== 'function') return;
    const controller = new AbortController();
    api.selectContext(evidenceContext);
    void restorePersistedEvidence(api, evidenceContext, { id: business.id, name: business.display_name,
      addedBy: 'Business owner' }, (recovered) => setModel((value) => mergeRecoveredEvidence(value, recovered)),
    controller.signal).catch((error: unknown) => {
      if (controller.signal.aborted || error instanceof DOMException && error.name === 'AbortError') return;
      setNotice(error instanceof Error ? `Saved documents could not be restored: ${error.message}`
        : 'Saved documents could not be restored.');
    });
    return () => controller.abort();
  }, [api, business?.display_name, business?.id, evidenceContext?.contextId]);

  const calculateVat = async (values: BusinessVatFactsInput) => {
    if (!business) throw new Error('No business is available for this identity.');
    setVatBusy(true);
    try {
      const bound = await bindBusinessVatFacts(values, { taxpayerId: business.id,
        ...(business.engagement_id ? { engagementId: business.engagement_id } : {}) });
      const calculation = await api.calculateVat(bound.request);
      setVatSnapshotSha256(bound.request.reviewedFactSnapshotSha256);
      setVatCalculation(calculation); setTaxReturn(undefined);
      setModel((value) => ({ ...value, calculation: { id: calculation.id, version: calculation.packageVersion,
        rulePackage: calculation.packageId, effectiveDate: bound.request.periodStart, status: 'ready-for-professional-review',
        netPositionLabel: `${formatKobo(calculation.liabilityKobo)} estimated payable`, traceAvailable: true,
        lines: calculation.trace.map((line) => ({ id: line.id, label: line.rule, amountLabel: formatKobo(line.resultKobo),
          basis: `Based on reviewed records · reference ${calculation.inputSnapshotSha256.slice(0, 8)}…`, sourceRefs: line.sourceIds })) },
        responsibilities: businessResponsibilitySteps('NOT_STARTED', 'No authoritative return created') }));
      setNotice('VAT estimate ready. Return preparation will remain unavailable until the required professional approval is complete.');
    } finally { setVatBusy(false); }
  };
  const runVatAction = async (action: 'prepare' | 'review' | 'approve' | 'sign') => {
    if (!taxReturn) return;
    setVatBusy(true);
    try {
      const updated = action === 'sign'
        ? await api.signReturn(taxReturn.id, 'business-vat-declaration-v1', 'business-owner-signatory')
        : await api.mutateReturn(taxReturn.id, action, vatCapacity(action));
      setTaxReturn(updated);
      setModel((value) => ({ ...value,
        responsibilities: businessResponsibilitySteps(updated.status, returnArtifactLabel(updated), value.responsibilities) }));
      setNotice(`VAT return action completed: ${action.replaceAll('-', ' ')}. Current state: ${updated.status}.`);
    } catch (error) { setNotice(error instanceof Error ? error.message : 'VAT return action failed.'); }
    finally { setVatBusy(false); }
  };
  const calculateCit = async (values: BusinessCitFactsInput) => {
    if (!business) throw new Error('No business is available for this identity.');
    const context = returnContextForObligation(business, 'CIT');
    if (!context) throw new Error('Company income tax is not configured for this business.');
    api.selectContext(context);
    setCitBusy(true);
    try {
      const request = await bindBusinessCitFacts(values, { taxpayerId: business.id,
        legalName: business.display_name, engagementId: context.scope.engagementId });
      const calculation = await api.calculateCit(request);
      setCitCalculation(calculation);
      setNotice(`CIT estimate ready. ${calculation.companyClassification === 'SMALL_COMPANY'
        ? 'The company met the candidate small-company thresholds.' : 'The company was treated as a standard company.'}`);
    } finally { setCitBusy(false); }
  };
  const createInvoice = async (facts: ReviewedInvoiceFactsInput) => {
    if (!business) { setNotice('No business is available for this identity.'); return; }
    const sequence = ++invoiceSequence.current;
    setInvoiceBusy(true); setReceipt(undefined); setLocalInvoice(undefined);
    try {
      if (invoiceContext) api.selectContext(invoiceContext);
      const created = await createOwnBusinessInvoiceDraft(api, business.id, facts);
      if (!invoiceCurrent(sequence)) return;
      setInvoice(created);
      setSavedInvoices((current) => [created, ...current]);
      setModel((value) => ({ ...value, invoices: [{ id: created.id, buyerName: facts.customer.registrationName,
        amountLabel: formatKobo(created.payableAmountKobo), invoiceNumber: created.invoiceNumber,
        state: 'needs-review', issue: 'Created from the invoice details you reviewed.' },
      ...value.invoices.filter((item) => item.id !== created.id)] }));
      setNotice('Invoice draft created and checked. Continue with preparation and review.');
      if (invoiceContext && typeof api.getLocalInvoice === 'function') {
        const value = await api.getLocalInvoice(created.id, invoiceContext.contextId);
        if (invoiceCurrent(sequence)) applyLocalInvoice(value);
      }
    } catch (error) { if (invoiceCurrent(sequence)) setNotice(error instanceof Error ? error.message : 'Invoice creation failed.'); throw error; }
    finally { if (invoiceCurrent(sequence)) setInvoiceBusy(false); }
  };
  const runInvoiceAction = async (action: 'prepare' | 'review' | 'approve' | 'mock-dispatch') => {
    if (!invoice) { setNotice('Create a reviewed invoice draft before using workflow actions.'); return; }
    const sequence = ++invoiceSequence.current;
    setInvoiceBusy(true);
    try {
      if (invoiceContext) api.selectContext(invoiceContext);
      if (localInvoice && invoiceContext && (action === 'review' || action === 'approve' || action === 'mock-dispatch')) {
        const confirmation = { expectedVersion: invoice.version, payloadSha256: invoice.regulatedPayload.payloadSha256, confirmed: true as const };
        const value = action === 'review' ? await api.reviewLocalInvoice(invoice.id, confirmation, invoiceContext.contextId)
          : action === 'approve' ? await api.approveLocalInvoice(invoice.id, confirmation, invoiceContext.contextId)
          : await api.submitLocalInvoice(invoice.id, confirmation, invoiceContext.contextId);
        if (invoiceCurrent(sequence)) { applyLocalInvoice(value); setNotice(value.submission?.message
          ?? (action === 'review' ? 'Invoice review saved.' : 'Invoice approval saved.')); }
        return;
      }
      if (action === 'mock-dispatch') {
        const result = await requestOwnBusinessInvoiceMock(api, invoice.id);
        if (!invoiceCurrent(sequence)) return;
        setInvoice(result.invoice); setReceipt(result.bridgeReceipt);
        setModel((value) => ({ ...value,
          invoices: value.invoices.map((item) => item.id === invoice.id ? { ...item, state: result.bridgeReceipt.state === 'MOCK_REJECTED' ? 'blocked' : 'bridge-mock-received',
            ...(result.bridgeReceipt.validationErrors.length ? { issue: result.bridgeReceipt.validationErrors.join(', ') } : { issue: undefined }) } : item),
          bridge: { ...value.bridge, reconciliationRequired: result.bridgeReceipt.state === 'MOCK_DISPATCH_AMBIGUOUS',
            retryAllowed: false, detail: result.bridgeReceipt.state === 'MOCK_RECEIVED'
              ? 'The local delivery test received the unchanged invoice file. No external acceptance was created.'
              : result.bridgeReceipt.state === 'MOCK_DISPATCH_AMBIGUOUS'
                ? 'The delivery-test result is unknown and must be checked before retrying.'
                : 'The local delivery test rejected the invoice file.' },
          submission: [...value.submission, { id: result.invoice.submissionRequestId ?? result.invoice.id,
            label: 'E-invoice delivery test', owner: 'Test filing connection', state: 'current', occurredAt: 'Just now',
            detail: `${result.bridgeReceipt.state}. Local test evidence only; no government or buyer outcome.` }] }));
        setNotice('Test delivery completed. Review the result below; this is not an official delivery confirmation.');
      } else {
        const updated = await mutateOwnBusinessInvoiceDraft(api, invoice.id, action);
        if (!invoiceCurrent(sequence)) return;
        setInvoice(updated); setSavedInvoices((current) => [updated, ...current.filter((item) => item.id !== updated.id)]);
        if (localInvoice) setLocalInvoice({ ...localInvoice, invoice: updated });
        setModel((value) => ({ ...value, invoices: value.invoices.map((item) => item.id === invoice.id
          ? { ...item, state: updated.state === 'REVIEWED' || updated.state === 'APPROVED' ? 'validated-for-mock' : 'needs-review', issue: undefined }
          : item) }));
        setNotice(`Invoice ${action} step completed. Current state: ${updated.state}.`);
      }
    } catch (error) { if (invoiceCurrent(sequence)) setNotice(error instanceof Error ? error.message : 'Invoice action failed.'); }
    finally { if (invoiceCurrent(sequence)) setInvoiceBusy(false); }
  };
  const shared: SharedJourneyPorts = {
    switchContext: () => undefined,
    loadEvidenceActivity: async (limit, signal) => {
      if (!business || !evidenceContext) return [];
      return (await api.evidenceActivity(business.id, limit, signal, evidenceContext.contextId)).events;
    },
    registerEvidence: async (file, _context, options) => {
      if (!business) { setNotice('No business is available for this identity.'); return; }
      const ownership = await detectEvidenceOwner(file, { id: business.id, name: business.display_name });
      let queuedJobId: string | undefined;
      try {
        const response = await api.upload({ client_id: business.id, filename: file.name,
          mime_type: file.type || inferMimeType(file.name), content_base64: await fileToBase64(file),
          ...(options?.modelAssistanceRequested ? { model_assistance_requested: true } : {}) }, options?.signal);
        queuedJobId = response.result.queued_job_id;
        setModel((value) => ({ ...value, evidence: [...value.evidence, { id: response.result.queued_job_id,
          name: file.name, sourceType: evidenceSource(file.name), state: 'checking', addedBy: 'Business owner',
          addedAt: 'Just now', hashLabel: 'Integrity check recorded', documentAvailable: true, taxpayerId: business.id,
          taxpayerName: business.display_name, ownershipState: ownership.state,
          ...(ownership.detectedName ? { ownershipProposal: { detectedName: ownership.detectedName,
            basis: ownership.detectedBy ?? 'browser-source-inspection', extractor: 'browser-source-inspection/2' } } : {}),
          ...(ownership.state === 'mismatch' ? { issue: `Possible wrong business: the source identifies ${ownership.detectedName}. Processing may continue, but its proposals are blocked until ownership is reviewed.` } : {}) }] }));
        setNotice(presentAppRecordCopy(ownership.state === 'mismatch' ? 'Evidence secured in ownership review. Processing is running, but its proposals will remain blocked.'
          : 'Evidence secured. Format-appropriate source checks and classification are running.', ios));
        options?.onAccepted?.();
        const job = await api.waitForJob(queuedJobId, { contextId: evidenceContext?.contextId });
        if (job.status === 'failed') {
          if (classificationSourceUnavailable(job)) {
            setModel((value) => ({ ...value, evidence: value.evidence.map((item) => item.id === queuedJobId
              ? settleEvidence(item, job) : item) }));
            setNotice('The stored source is unavailable for review. Check its details for recovery options; manual tax amounts remain available.');
            return;
          }
          const promoted = await api.prepareClassificationEvidenceForHumanReview(file, queuedJobId, undefined, evidenceContext);
          setModel((value) => ({ ...value, evidence: value.evidence.map((item) => item.id === queuedJobId
            ? { ...settleEvidence(item, job), serverEvidenceId: promoted.evidenceId,
              ...(promoted.duplicateOfEvidenceId ? { duplicateOfEvidenceId: promoted.duplicateOfEvidenceId } : {}),
              issue: `${settleEvidence(item, job).issue ?? ''} Review the source and classify it manually if Amoye’s assessment is not correct.`.trim() }
            : item) }));
          setNotice('Amoye could not create reliable proposals. The secured source is available for your review and manual classification.'); return;
        }
        const result = await api.result(queuedJobId, undefined, evidenceContext?.contextId);
        const promoted = await api.promoteClassificationEvidence(file, queuedJobId, undefined, evidenceContext);
        const extractedOwnership = evaluateExtractedOwnership(result.document_analysis,
          { id: business.id, name: business.display_name });
        setModel((value) => {
          const current = value.evidence.find((item) => item.id === queuedJobId);
          const ownership = current ? applyExtractedOwnership(settleEvidence(current, job), result,
            { id: business.id, name: business.display_name }) : undefined;
          const proposals = reconciliationFromResult(result, file.name, queuedJobId).map((proposal) => {
            const serverProposals = (promoted.proposalsByItemId?.[proposal.id] ?? [])
              .filter((candidate) => !candidate.fieldPath.endsWith('.ownership_state'));
            const linked = { ...proposal, serverProposalIds: serverProposals.length
              ? serverProposals.map((candidate) => candidate.id)
              : promoted.proposalIdsByTransactionId[proposal.id] ?? [], serverProposals };
            return applyEvidenceProposalBlockers(linked, ownership?.evidence, promoted.duplicateOfEvidenceId);
          });
          return { ...value,
            evidence: value.evidence.map((item) => item.id === queuedJobId
              ? { ...applyEvidenceDuplicatePresentation(ownership?.evidence ?? settleEvidence(item, job), promoted.duplicateOfEvidenceId), serverEvidenceId: promoted.evidenceId,
                ...(promoted.processingGrantId ? { processingGrantId: promoted.processingGrantId } : {}),
                ...((promoted.proposalsByItemId?.[queuedJobId!] ?? []).some((proposal) => proposal.fieldPath.endsWith('.ownership_state'))
                  ? { serverOwnershipProposals: (promoted.proposalsByItemId?.[queuedJobId!] ?? [])
                    .filter((proposal) => proposal.fieldPath.endsWith('.ownership_state')) } : {}) } : item),
            reconciliation: [...proposals, ...value.reconciliation] };
        });
        setNotice(ownership.state === 'mismatch' || extractedOwnership?.state === 'mismatch'
          ? 'A possible taxpayer mismatch needs review. View the document, then confirm or flag its ownership.'
          : `${result.transactions.length} source-derived proposal${result.transactions.length === 1 ? '' : 's'} ready for human review.`);
      } catch (error) {
        if (queuedJobId) setModel((value) => ({ ...value, evidence: value.evidence.map((item) => item.id === queuedJobId
          ? settleEvidenceWorkflowError(item, error) : item) }));
        setNotice(error instanceof Error ? error.message : presentAppRecordCopy('Evidence registration failed.', ios));
        if (!queuedJobId) throw error;
      }
    },
    reviewReconciliation: async (item, decision, correction) => {
      if (!item.serverProposalIds?.length) throw new Error('This item is not linked to a durable review record.');
      if (decision === 'correct') {
        if (!item.serverProposals?.length || !correction) throw new Error('Enter and save a correction before continuing.');
        await api.correctEvidenceProposals(item.serverProposals, correction);
      } else await api.reviewEvidenceProposals(item.serverProposalIds, decision);
      setModel((value) => {
        const reconciliation: TrackB1Model['reconciliation'] = value.reconciliation.map((entry) => entry.id === item.id
          ? { ...entry, ...(correction?.ntaCode ? { ntaCode: correction.ntaCode } : {}),
            ...(correction?.vatAmountKobo ? { vatAmountKobo: correction.vatAmountKobo } : {}),
            ...(correction?.evidenceCategory ? { evidenceCategory: correction.evidenceCategory } : {}),
            state: decision === 'correct' ? 'corrected-by-human' : decision === 'accept' ? 'accepted-by-human'
            : decision === 'exclude' ? 'excluded-by-human' : 'blocked' }
          : entry);
        return { ...value, reconciliation,
          evidence: applyEvidenceReviewProgress(value.evidence, reconciliation, item.evidenceId) };
      });
    },
    unlinkEvidence: async (item, input) => {
      if (item.processingGrantId && item.serverEvidenceId) {
        await api.revokeEvidenceProcessing(item.processingGrantId, item.serverEvidenceId);
      }
      await api.deleteClassificationEvidence(item.id, input.reason, input.batchId);
      setTaxReturn(undefined); setVatCalculation(undefined); setCitCalculation(undefined); setVatSnapshotSha256(undefined);
      setModel((value) => {
        const purged = purgeEvidenceUsage(value, item.id);
        return { ...purged, calculation: { ...purged.calculation, status: 'draft', netPositionLabel: 'Not calculated', lines: [] },
          responsibilities: businessResponsibilitySteps('NOT_STARTED', presentAppRecordCopy('Invalidated after evidence was deleted', ios)) };
      });
      setNotice(`${item.name} was permanently deleted. Its stored file and derived proposals will not return after sign-in.`);
    },
    viewEvidence: async (item) => api.evidenceDocument(item.id),
    reviewEvidenceOwnership: async (item, decision) => {
      if (!item.serverOwnershipProposals?.length) throw new Error(item.duplicateOfEvidenceId
        ? 'This copy duplicates an earlier secured source. Resolve ownership on the original source.'
        : 'Ownership review is not ready yet. Resume processing before recording this decision.');
      await api.reviewEvidenceOwnershipProposals(item.serverOwnershipProposals, decision);
      if (decision === 'flag-wrong-taxpayer') {
        if (item.processingGrantId && item.serverEvidenceId) {
          await api.revokeEvidenceProcessing(item.processingGrantId, item.serverEvidenceId);
        }
        await api.deleteClassificationEvidence(item.id, 'wrong-taxpayer');
        setTaxReturn(undefined); setVatCalculation(undefined); setCitCalculation(undefined); setVatSnapshotSha256(undefined);
        setModel((value) => purgeEvidenceUsage(value, item.id));
        setNotice(`${item.name} was flagged as belonging to the wrong taxpayer and permanently deleted.`); return;
      }
      setModel((value) => ({ ...value,
        evidence: value.evidence.map((entry) => entry.id === item.id ? { ...entry, state: 'ready-for-review',
          ownershipState: 'confirmed', issue: 'Ownership confirmed by the business owner. Review the extracted proposals next.' } : entry),
        reconciliation: value.reconciliation.map((entry) => entry.evidenceId === item.id && entry.state === 'blocked'
          ? { ...entry, state: 'needs-review', dispositionReason: undefined } : entry) }));
      setNotice(`Ownership confirmed for ${item.name}. Its proposals are ready for review.`);
    },
    humanClassifyEvidence: async (item, input) => {
      if (!item.serverEvidenceId) throw new Error('The secured source is not ready for a recorded human decision yet.');
      await api.humanClassifyEvidence(item.serverEvidenceId, { ...input, reviewedAt: new Date().toISOString() });
      setTaxReturn(undefined); setVatCalculation(undefined); setCitCalculation(undefined); setVatSnapshotSha256(undefined);
      setModel((value) => {
        const reconciliation = value.reconciliation.map((entry) => entry.evidenceId === item.id && entry.state === 'blocked'
          && entry.periodState !== 'mismatch' && !item.duplicateOfEvidenceId
          ? { ...entry, state: 'needs-review' as const, dispositionReason: undefined } : entry);
        const evidence = value.evidence.map((entry) => entry.id === item.id ? { ...entry,
          state: entry.periodState === 'mismatch' ? 'blocked' as const : 'reviewed' as const,
          ownershipState: 'confirmed' as const, classificationCategory: input.evidenceCategory,
          issue: entry.periodState === 'mismatch'
            ? `Category recorded as ${input.evidenceCategory.replaceAll('_', ' ')}, but this source is still outside the selected filing period. Move it to the correct period or remove it from this return.`
            : `Human classification recorded: ${input.evidenceCategory.replaceAll('_', ' ')}. The automated assessment remains in the audit trail.` }
          : entry);
        return { ...value, evidence: applyEvidenceReviewProgress(evidence, reconciliation, item.id), reconciliation,
        calculation: { ...value.calculation, status: 'draft', netPositionLabel: 'Not calculated', lines: [] },
        responsibilities: businessResponsibilitySteps('NOT_STARTED', presentAppRecordCopy('Invalidated after evidence classification changed', ios)) };
      });
      setNotice(`${item.name} is now linked as ${input.evidenceCategory.replaceAll('_', ' ')} based on your recorded review.`);
    },
    performResponsibilityAction: (action) => {
      if (action === 'request-mock-submission') {
        setNotice('Filing is not available yet. Your prepared return remains saved and you can continue reviewing it.');
        return;
      }
      const vatAction = action === 'professional-review' ? 'review' : action === 'declare-sign' ? 'sign'
        : action;
      void runVatAction(vatAction);
    },
    reconcileBridge: () => setModel((value) => ({ ...value, bridge: { ...value.bridge,
      reconciliationRequired: false, retryAllowed: true, detail: 'The test request was checked without creating an official invoice outcome.' } })),
    retryBridge: () => setNotice('The unchanged invoice was queued again for test delivery.'),
    setIntelligenceEnabled: (enabled) => setModel((value) => ({ ...value,
      intelligence: { ...value.intelligence, enabled, status: enabled ? 'ready' : 'off' } })),
    loadHomeSuggestions: signal => api.loadHomeSuggestions({ rail: 'tax_return' }, signal),
    queryIntelligence: async (prompt, consentEvidenceId, signal, historyMode = 'EPHEMERAL') => {
      const response = await api.queryAssistantIntelligence({ rail: 'tax_return', prompt,
        consentEvidenceId, historyMode }, signal);
      return { answer: response.answer, citations: response.citations.map(({
        sourceId, sourceVersion, locator, title, authority, sourceUri,
      }) => ({ sourceId, sourceVersion, locator, title, authority, sourceUri })),
        limitations: response.limitations, generatedAt: response.generatedAt,
        modelId: response.modelId, modelVersion: response.modelVersion,
        activities: response.activities, navigationIntents: response.navigationIntents,
        historyMode: response.conversation.historyMode, retainedUntil: response.conversation.retainedUntil };
    },
    loadIntelligenceHistory: async (consentEvidenceId, signal) => (await api.loadAssistantHistory({
      rail: 'tax_return', consentEvidenceId }, signal)).turns.map((turn) => ({ ...turn,
        response: { answer: turn.response.answer,
          citations: turn.response.citations.map(({ sourceId, sourceVersion, locator, title, authority, sourceUri }) =>
            ({ sourceId, sourceVersion, locator, title, authority, sourceUri })),
          limitations: turn.response.limitations, generatedAt: turn.response.generatedAt,
          modelId: turn.response.modelId, modelVersion: turn.response.modelVersion,
          activities: turn.response.activities, navigationIntents: turn.response.navigationIntents,
          historyMode: turn.response.conversation.historyMode,
          retainedUntil: turn.response.conversation.retainedUntil } })),
    deleteIntelligenceHistory: (consentEvidenceId, signal) => api.deleteAssistantHistory({
      rail: 'tax_return', consentEvidenceId }, signal),
  };
  const vatInteraction = <BusinessVatReturnInteraction busy={vatBusy} calculation={vatCalculation} taxReturn={taxReturn}
    filingActionsAvailable={false} reviewedFactSnapshotSha256={vatSnapshotSha256}
    onCalculate={calculateVat} onAction={runVatAction} />;
  const citInteraction = <BusinessCitReturnInteraction businessName={business?.display_name ?? ''}
    busy={citBusy} calculation={citCalculation} onCalculate={calculateCit} />;
  const returnObligations = configuredObligationsForSubject(business).map((obligation) =>
    toReturnCentreObligation(obligation, obligation.id === 'VAT'
      ? { ...(taxReturn ? { taxReturn } : {}), interaction: vatInteraction }
      : obligation.id === 'CIT' ? { interaction: citInteraction } : {}));
  const selectReturnObligation = (obligation: TaxObligationId) => {
    const context = returnContextForObligation(business, obligation);
    if (context) api.selectContext(context);
    setSelectedReturnObligation(obligation);
  };
  const returnCentre = <ReturnCentre subjectName={business?.display_name ?? model.context.subjectName}
    selectedObligation={selectedReturnObligation} onSelectObligation={selectReturnObligation}
    obligations={returnObligations} />;
  return <>{notice && <p aria-live="polite" className="am-session-only">{notice}</p>}<TrackB1Journey model={model}
    conversationScope={`${business?.id ?? model.context.id}:${selectedReturnObligation}:${returnContextForObligation(business, selectedReturnObligation)?.contextId ?? 'unresolved'}`}
    taxInteraction={returnCentre}
    invoiceInteraction={<div id="business-e-invoice-editor" tabIndex={-1}>
      {invoiceRestoring ? <p aria-live="polite">Loading saved invoices…</p> : <>
      {savedInvoices.length > 0 && <label>Saved invoice <Select value={invoice?.id ?? ''} disabled={invoiceBusy}
        onChange={(event) => void selectSavedInvoice(event.target.value)}>
        {!invoice && <option value="">New invoice draft</option>}
        {savedInvoices.map((item) => <option key={item.id} value={item.id}>{item.invoiceNumber} · {formatKobo(item.payableAmountKobo)}</option>)}
      </Select></label>}
      {invoice && <Button type="button" tone="secondary" disabled={invoiceBusy} onClick={() => {
        invoiceSequence.current += 1; setInvoice(undefined); setLocalInvoice(undefined); setReceipt(undefined);
      }}>New invoice</Button>}
      <OwnBusinessInvoiceInteraction key={invoice?.id ?? 'new-invoice'}
      businessName={business?.display_name ?? ''} busy={invoiceBusy}
      invoice={invoice ? { id: invoice.id, version: invoice.version, state: invoice.state, invoiceNumber: invoice.invoiceNumber,
        payableAmountKobo: invoice.payableAmountKobo, payloadSha256: invoice.regulatedPayload.payloadSha256,
        payloadBase64: invoice.regulatedPayload.payloadBase64,
        validationState: invoice.regulatedPayload.validation.state } : undefined}
      localSubmission={localInvoice?.submission} localConfirmationRequired={Boolean(localInvoice)}
      onRefresh={() => refreshInvoice(false)} onRetry={() => refreshInvoice(true)}
      receipt={receipt} onAction={runInvoiceAction} onCreate={createInvoice} />
      </>}
    </div>}
    ports={{
    ...shared,
    openFreezeCase: (id) => setNotice(`Opened buyer payment blocker ${id}.`),
    createInvoice: () => setNotice('Complete the e-invoice form to create and check the invoice draft.'),
    reviewInvoice: () => {
      const editor = document.getElementById('business-e-invoice-editor');
      editor?.focus();
      editor?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    },
  }} /></>;
}

function buildInitialBusinessModel(business: PersonaWorkspaceProps['subjects'][number] | undefined): TrackB1Model {
  const template = structuredClone(trackB1Fixture);
  const subjectName = business?.display_name ?? 'Business taxpayer';
  return { ...template,
    context: { ...template.context, subjectName, workspaceName: subjectName },
    deadlines: filingDeadlineNoticesForSubject(business),
    evidence: [], reconciliation: [],
    calculation: { ...template.calculation, id: 'business-calculation-not-created', version: 'Not calculated',
      rulePackage: 'No calculation run', effectiveDate: 'Not calculated', status: 'draft',
      netPositionLabel: 'Not calculated', traceAvailable: false, lines: [] },
    responsibilities: businessResponsibilitySteps('NOT_STARTED', 'Not created'),
    submission: [], freezeCases: [], invoices: [],
    bridge: { ...template.bridge, retryAllowed: false, reconciliationRequired: false,
      detail: 'No filing or e-invoice request has been made for this business.' },
    intelligence: { ...template.intelligence, enabled: true, status: 'ready', insights: [], citations: [] },
  };
}

function evidenceSource(name: string): 'controlled-csv' | 'receipt' | 'bank-statement' | 'pos-export' {
  const extension = name.toLowerCase().split('.').at(-1);
  if (extension === 'pdf') return 'bank-statement';
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension ?? '')) return 'receipt';
  if (['xlsx', 'json'].includes(extension ?? '')) return 'pos-export';
  return 'controlled-csv';
}

function inferMimeType(name: string): string {
  const extension = name.toLowerCase().split('.').at(-1);
  return extension === 'csv' ? 'text/csv' : extension === 'json' ? 'application/json'
    : extension === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : extension === 'pdf' ? 'application/pdf' : ['jpg', 'jpeg'].includes(extension ?? '') ? 'image/jpeg'
        : extension === 'png' ? 'image/png' : extension === 'webp' ? 'image/webp' : 'application/octet-stream';
}

function vatCapacity(action: 'prepare' | 'review' | 'approve'): string {
  return action === 'prepare' ? 'business-owner-preparer' : action === 'review' ? 'qualified-business-reviewer'
    : 'business-owner-approver';
}

function returnArtifactLabel(value: TaxReturnResource): string {
  return value.artifactSha256 ? `Return artifact · …${value.artifactSha256.slice(-8)}` : `Return draft · version ${value.version}`;
}

function businessResponsibilitySteps(status: string, artifactVersion: string,
  previous: TrackB1Model['responsibilities'] = []): TrackB1Model['responsibilities'] {
  const previouslyCompleted = new Set(previous.filter((step) => step.state === 'complete').map((step) => step.action));
  const signed = status === 'SIGNED' || status === 'SUBMISSION_REQUESTED';
  const submitted = status === 'SUBMISSION_REQUESTED';
  const state = (action: TrackB1Model['responsibilities'][number]['action']) => {
    if (action === 'prepare') return signed ? previouslyCompleted.has(action) ? 'complete' : 'invalidated'
      : ['PREPARED', 'REVIEWED', 'APPROVED'].includes(status) ? 'complete' : status === 'CALCULATED' ? 'ready' : 'waiting';
    if (action === 'professional-review') return signed ? previouslyCompleted.has(action) ? 'complete' : 'invalidated'
      : ['REVIEWED', 'APPROVED'].includes(status) ? 'complete' : status === 'PREPARED' ? 'ready' : 'blocked';
    if (action === 'approve') return signed ? previouslyCompleted.has(action) ? 'complete' : 'invalidated'
      : status === 'APPROVED' ? 'complete' : status === 'REVIEWED' ? 'ready' : 'blocked';
    if (action === 'declare-sign') return signed ? 'complete'
      : ['CALCULATED', 'PREPARED', 'APPROVED'].includes(status) ? 'ready' : 'blocked';
    return submitted ? 'complete' : status === 'SIGNED' ? 'ready' : 'blocked';
  };
  return [
    { id: 'b1-rs-prepare', action: 'prepare', actorLabel: 'Authorised business owner or operator · Preparer',
      state: state('prepare'), artifactVersion, explanation: 'This preparation step records the reviewed figures and calculation in the return.' },
    { id: 'b1-rs-review', action: 'professional-review', actorLabel: 'Authorised business reviewer or adviser · Reviewer',
      state: state('professional-review'), artifactVersion, explanation: 'Use the adviser path where the business policy or tax treatment requires professional review.' },
    { id: 'b1-rs-approve', action: 'approve', actorLabel: 'Authorised business approver', state: state('approve'), artifactVersion,
      explanation: 'Approval is explicit and remains distinct from the taxpayer declaration.' },
    { id: 'b1-rs-sign', action: 'declare-sign', actorLabel: 'Authorised business signatory', state: state('declare-sign'), artifactVersion,
      explanation: 'The signatory declares the exact return version after the required calculation, preparation and approval steps.' },
    { id: 'b1-rs-submit', action: 'request-mock-submission', actorLabel: 'Authorised submitter',
      state: state('request-mock-submission'), artifactVersion,
      explanation: 'This creates a local delivery test only and cannot create an NRS filing outcome.' },
  ];
}
